import {
  ClockIcon
} from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  ChecklistData,
  ChecklistItem,
  ChecklistTemplate,
} from "../../db/schema";
import { processAudioFile, updateCaseWithBlankContent } from "../../services/audioProcessingService";
import NewCaseDropdown from "../common/NewCaseDropdown";
import AudioPlayback from "../Menu/AudioPlayback";
import UploadModal from "../Menu/UploadModal";

interface ChecklistViewProps {
  template: ChecklistTemplate;
  data: ChecklistData;
  onChange: (updated: ChecklistData) => void;
  onComplete: () => void;
  isReadOnly?: boolean;
  createdAt?: string;
  updatedAt?: string;
  caseId?: string;
  setCaseContent?: React.Dispatch<
    React.SetStateAction<{ data: string } | undefined>
  >;
  handleSelectedCase?: (caseId: string) => void;
}

const ChecklistView: React.FC<ChecklistViewProps> = ({
  template,
  data,
  onChange,
  onComplete,
  isReadOnly = false,
  createdAt,
  updatedAt,
  caseId,
  setCaseContent,
  handleSelectedCase,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [localData, setLocalData] = useState<ChecklistData>(data);
  const recordModalRef = useRef<HTMLDialogElement>(null);
  const uploadModalRef = useRef<HTMLDialogElement>(null);

  // Sync local data when the data prop changes (e.g., when returning to a case with updated checklist)
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  // Group items by category
  const groupedItems = template.items.reduce((groups, item) => {
    const category = item.category || "General";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, typeof template.items>);

  // Sort items within each category by order
  Object.keys(groupedItems).forEach((category) => {
    groupedItems[category].sort((a, b) => a.order - b.order);
  });

  const handleItemChange = (itemId: string, checked: boolean, additionalNotes?: string) => {
    if (isReadOnly) return;

    const currentItemData = localData.items[itemId] || {};

    // Only update checked_at if the checked status is actually changing
    const isCheckStatusChanging = currentItemData.checked !== checked;

    const newData = {
      ...localData,
      items: {
        ...localData.items,
        [itemId]: {
          checked,
          // Keep existing checked_at if only notes are changing, otherwise update it
          checked_at: isCheckStatusChanging
            ? (checked ? new Date().toISOString() : undefined)
            : currentItemData.checked_at,
          additionalNotes: additionalNotes !== undefined ? additionalNotes : currentItemData.additionalNotes,
        },
      },
    };

    setLocalData(newData);
    onChange(newData);
  };

  const getTotalItems = () => template.items.length;
  const getCompletedItems = () =>
    Object.values(localData.items).filter((item) => item.checked).length;

  const getProgress = () => {
    const total = getTotalItems();
    const completed = getCompletedItems();
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getCategoryProgress = (items: ChecklistItem[]) => {
    const total = items.length;
    const completed = items.filter(
      (item) => localData.items[item.id]?.checked
    ).length;
    return { completed, total };
  };

  const canComplete = () => {
    const requiredItems = template.items.filter((item) => item.required);
    return requiredItems.every((item) => localData.items[item.id]?.checked);
  };

  const handleRecordAudio = () => {
    recordModalRef.current?.showModal();
  };

  const handleUploadAudio = () => {
    uploadModalRef.current?.showModal();
  };

  const handleBlankReport = async () => {
    try {
      // First, complete the current checklist
      onComplete();

      // Use the shared service to update the existing case
      if (caseId) {
        await updateCaseWithBlankContent(caseId, setCaseContent, navigate);
      } else {
        console.error("No case ID available for blank report");
      }
    } catch (error) {
      console.error("Error updating case:", error);
    }
  };

  const handleAudioFileChange = async (
    filesOrEvent:
      | File
      | Blob
      | React.ChangeEvent<HTMLInputElement>
      | Array<{
          file: File;
          audioType: "narrative" | "interaction";
          speakers: { name: string; role: string }[];
          languages: string[];
          additionalContext: string;
        }>,
    optionsOrAudioType?:
      | string
      | {
          includeTimestamps: boolean;
          detailLevel: "detailed" | "standard" | "concise";
        }
      | {
          speakers: { name: string; role: string }[];
          includeTimestamps: boolean;
          additionalContext: string;
          detailLevel: "detailed" | "standard" | "concise";
        },
    methodOrLegacy?: string,
    supplementalAudioOrTemplateId?: File | string | null
  ) => {
    if (!caseId) return;

    // Separate supplementalAudio (File) from templateId (string)
    const supplementalAudio = supplementalAudioOrTemplateId instanceof File ? supplementalAudioOrTemplateId : undefined;
    const reportTemplateId = typeof supplementalAudioOrTemplateId === "string" ? supplementalAudioOrTemplateId : undefined;

    await processAudioFile(
      filesOrEvent,
      optionsOrAudioType,
      methodOrLegacy,
      supplementalAudio,
      {
        reportTemplateId,
        user,
        createNewCase: false,
        existingCaseId: caseId,
        setCaseContent,
        onComplete,
        // Use handleSelectedCase to re-fetch the case instead of navigate
        // This ensures the case is updated when already on the case URL
        navigate: handleSelectedCase
          ? () => handleSelectedCase(caseId)
          : () => navigate(`/cases/${caseId}`),
        onError: (error) => {
          console.error("Audio processing failed:", error);
        },
      }
    );
  };

  return (
    <>
      <div className="max-w-4xl w-8/12 min-w-96 mx-auto p-6">
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300">
          {/* Header */}
          <div className="px-6 py-4 border-b border-base-300">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-base-content">
                  {template.name}
                </h1>
                {template.description && (
                  <p className="text-gray-600 dark:text-base-content/70 mt-1">{template.description}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">
                  {getProgress()}%
                </div>
                <div className="text-sm text-base-content/60 whitespace-nowrap">
                  {getCompletedItems()} of {getTotalItems()} completed
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="bg-base-300 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgress()}%` }}
                ></div>
              </div>
            </div>

            {/* Timestamps */}
            {(createdAt || updatedAt) && (
              <div className="flex gap-4 mt-4 text-xs text-base-content/60">
                {createdAt && (
                  <div className="flex items-center gap-1">
                    <ClockIcon className="w-4 h-4" />
                    Started: {new Date(createdAt).toLocaleDateString()}
                  </div>
                )}
                {updatedAt && updatedAt !== createdAt && (
                  <div className="flex items-center gap-1">
                    <ClockIcon className="w-4 h-4" />
                    Updated: {new Date(updatedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Checklist Items */}
          <div className="px-6 py-4">
            {Object.entries(groupedItems).map(([category, items]) => {
              const { completed, total } = getCategoryProgress(items);
              return (
                <div key={category} className="mb-6 last:mb-0">
                  {Object.keys(groupedItems).length > 1 && (
                    <div className="flex items-center justify-between mb-3 border-b border-base-200 pb-1">
                      <h3 className="font-semibold text-lg text-base-content">
                        {category}
                      </h3>
                      <span className="text-sm text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 px-2 py-1 rounded-full font-medium">
                        {completed}/{total}
                      </span>
                    </div>
                  )}

                  <div className="space-y-3">
                    {items.map((item) => (
                      <div key={item.id} className="space-y-2">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id={item.id}
                            checked={localData.items[item.id]?.checked || false}
                            onChange={(e) =>
                              handleItemChange(item.id, e.target.checked)
                            }
                            disabled={isReadOnly}
                            className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-base-300 focus:ring-blue-500"
                          />
                          <label
                            htmlFor={item.id}
                            className="flex-1 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-sm ${
                                  localData.items[item.id]?.checked
                                    ? "line-through text-base-content/60"
                                    : "text-base-content"
                                }`}
                              >
                                {item.text}
                              </span>
                              {item.required && (
                                <span className="text-xs bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-400 px-2 py-0.5 rounded-full">
                                  Required
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-xs text-gray-600 dark:text-base-content/70 mt-1">
                                {item.description}
                              </p>
                            )}
                            {localData.items[item.id]?.checked_at && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                Completed:{" "}
                                {new Date(
                                  localData.items[item.id].checked_at!
                                ).toLocaleString()}
                              </p>
                            )}
                          </label>
                        </div>
                        {/* Additional Notes Textarea */}
                        {localData.items[item.id]?.checked && item.additionalNotesEnabled && (
                          <div className="ml-7 mt-2">
                            <label className="block text-xs font-medium text-gray-700 dark:text-base-content/80 mb-1">
                              Additional Notes
                            </label>
                            <textarea
                              value={localData.items[item.id]?.additionalNotes || ""}
                              onChange={(e) =>
                                handleItemChange(item.id, true, e.target.value)
                              }
                              disabled={isReadOnly}
                              placeholder="Add any additional details or context..."
                              rows={3}
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-base-200 disabled:cursor-not-allowed"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {!isReadOnly && (
            <div className="px-6 py-4 border-t border-base-300 bg-base-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-base-content/70">
                  {canComplete() ? (
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      ✓ All required items completed
                    </span>
                  ) : (
                    <span>Complete all required items to proceed</span>
                  )}
                </div>

                <NewCaseDropdown
                  onRecordAudio={handleRecordAudio}
                  onUploadAudio={handleUploadAudio}
                  onBlankReport={handleBlankReport}
                  showChecklist={false}
                  buttonText="Complete Checklist"
                  buttonVariant="primary"
                  className="w-auto dropdown-top"
                  isMenuCollapsed={false}
                  disabled={!canComplete()}
                  disabledTooltip="Please complete all required items before proceeding"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AudioPlayback
        modalRef={recordModalRef}
        handleAudioFileChange={handleAudioFileChange}
      />
      <UploadModal
        modalRef={uploadModalRef}
        onUpload={handleAudioFileChange}
      />
    </>
  );
};

export default ChecklistView;
